import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store/auth';

/**
 * PrivateRoute Component
 *
 * A route guard that protects private routes from unauthenticated access.
 * Redirects to login if user has no valid access token, preserving the
 * original URL as returnUrl for redirect after successful authentication.
 *
 * ## Features
 * - **Token validation**: Checks for accessToken in auth store
 * - **Auto-redirect**: Sends unauthenticated users to /login
 * - **Return URL**: Preserves original URL for post-login redirect
 * - **Nested routes**: Supports React Router's Outlet pattern
 *
 * @example
 * Basic usage with nested routes
 * ```tsx
 * <Route element={<PrivateRoute />}>
 *   <Route path="/" element={<HomePage />} />
 *   <Route path="/profile" element={<ProfilePage />} />
 * </Route>
 * ```
 */
export function PrivateRoute() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const location = useLocation();

  if (!isAuthenticated) {
    const returnUrl = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`}
        replace
      />
    );
  }

  return <Outlet />;
}
